import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import {
	GetItineraryImportResponse,
	GetItineraryImportsResponse,
	PostItineraryImportResponse,
} from '@orbit/api-types'
import { useMutation, useQuery } from '@tanstack/react-query'

interface CreateImportVars {
	tripId: string
	text?: string
	files: File[]
}

export const useCreateItineraryImport = () => {
	return useMutation<PostItineraryImportResponse, Error, CreateImportVars>({
		mutationFn: async ({ tripId, text, files }) => {
			const formData = new FormData()
			if (text && text.trim().length > 0) {
				formData.append('text', text)
			}
			files.forEach(file => formData.append('files', file))

			const response = await api.post<PostItineraryImportResponse>(
				`/trips/${tripId}/itinerary-import`,
				formData,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					timeout: 120000,
				},
			)
			return response.data
		},
	})
}

export const useRetryItineraryImport = () => {
	return useMutation<PostItineraryImportResponse, Error, { tripId: string; importId: string }>({
		mutationFn: async ({ tripId, importId }) => {
			const response = await api.post<PostItineraryImportResponse>(
				`/trips/${tripId}/itinerary-imports/${importId}/retry`,
			)
			return response.data
		},
	})
}

interface UseItineraryImportStatusOptions {
	tripId: string | undefined
	importId: string | undefined
	enabled?: boolean
}

export const useItineraryImportStatus = ({
	tripId,
	importId,
	enabled = true,
}: UseItineraryImportStatusOptions) => {
	return useQuery<GetItineraryImportResponse>({
		queryKey: queryKeys.itineraryImport(tripId, importId),
		queryFn: async () => {
			const response = await api.get<GetItineraryImportResponse>(
				`/trips/${tripId}/itinerary-imports/${importId}`,
			)
			return response.data
		},
		enabled: enabled && !!tripId && !!importId,
		refetchInterval: query => {
			const data = query.state.data
			if (!data) return 2000
			if (data.status === 'Pending' || data.status === 'Processing') {
				const elapsed = Date.now() - data.createdAt * 1000
				return elapsed > 30000 ? 5000 : 2000
			}
			return false
		},
		retry: 1,
	})
}

export const useTripItineraryImports = (tripId: string | undefined) => {
	return useQuery<GetItineraryImportsResponse>({
		queryKey: queryKeys.itineraryImports(tripId),
		queryFn: async () => {
			const response = await api.get<GetItineraryImportsResponse>(
				`/trips/${tripId}/itinerary-imports`,
			)
			return response.data
		},
		enabled: !!tripId,
		// The banner has to notice an import started on another page, so the list
		// refetches whenever the itinerary mounts instead of serving cached items.
		staleTime: 0,
		refetchOnMount: 'always',
		refetchInterval: query => {
			const items = query.state.data?.items ?? []
			const hasActive = items.some(
				item => item.status === 'Pending' || item.status === 'Processing',
			)
			return hasActive ? 2500 : false
		},
		retry: 1,
	})
}
